import { useState } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from '@/shared/hooks'
import { LocalizedLink } from '@/shared/components'
import { toast } from 'react-toastify'
import { Loader2, AlertCircle, ArrowLeft, Check, X, ExternalLink } from 'lucide-react'
import { Sidebar, ConfirmModal } from '@/shared/components'
import { useUserStoryByShortId } from '../hooks/use-user-story'
import { useValidateUserStory } from '../hooks/use-validate-user-story'
import { useRejectUserStory } from '../hooks/use-reject-user-story'
import { useTestOk, useTestKo } from '../hooks/use-test-user-story'
import { RejectUSModal } from '../components/reject-us-modal'
import { TestKOModal } from '../components/test-ko-modal'
import { UserStoryExchangeZone } from '../components/exchange-zone'
import { cn } from '@/shared/lib/utils'
import type { USStatus } from '@/shared/types'

// US status badge component (pill style)
function USStatusBadge({ status }: { status: USStatus }) {
  const { t } = useTranslation()

  const styles: Record<USStatus, { bg: string; text: string }> = {
    pending_validation: { bg: 'bg-orange-50', text: 'text-orange-500' },
    validated: { bg: 'bg-green-50', text: 'text-green-600' },
    to_do: { bg: 'bg-slate-100', text: 'text-slate-600' },
    in_progress: { bg: 'bg-violet-50', text: 'text-violet-600' },
    testing: { bg: 'bg-amber-50', text: 'text-amber-600' },
    done: { bg: 'bg-green-50', text: 'text-green-600' },
  }

  const style = styles[status] || styles.to_do

  return (
    <span className={cn('rounded-full px-2.5 py-1 text-xs font-medium', style.bg, style.text)}>
      {t(`feature.usStatus.${status}`)}
    </span>
  )
}

// Spec field component
function SpecField({ label, value }: { label: string; value?: string }) {
  if (!value) return null

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-foreground-muted">{label}</span>
      <p className="whitespace-pre-wrap text-sm text-foreground">{value}</p>
    </div>
  )
}

export function UserStoryDetailPage() {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { shortId } = useParams<{ shortId: string }>()

  const { data: userStory, isLoading, error } = useUserStoryByShortId(shortId)
  const validateMutation = useValidateUserStory()
  const rejectMutation = useRejectUserStory()
  const testOkMutation = useTestOk()
  const testKoMutation = useTestKo()

  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
  const [isTestKoModalOpen, setIsTestKoModalOpen] = useState(false)
  const [isTestOkModalOpen, setIsTestOkModalOpen] = useState(false)

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar />
        <main className="flex flex-1 items-center justify-center bg-background-secondary">
          <Loader2 className="h-8 w-8 animate-spin text-foreground-muted" />
        </main>
      </div>
    )
  }

  // Error state
  if (error || !userStory) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar />
        <main className="flex flex-1 flex-col items-center justify-center gap-4 bg-background-secondary">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <p className="text-foreground-muted">{t('common.error')}</p>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-accent hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </button>
        </main>
      </div>
    )
  }

  const feature = userStory.feature
  const project = feature?.project
  const team = project?.team

  // Check if user can validate (pending_validation status)
  const canValidate = userStory.status === 'pending_validation'

  // Check if user can test (testing status)
  const canTest = userStory.status === 'testing'

  // Handle validate button click
  const handleValidate = () => {
    validateMutation.mutate(userStory.id)
  }

  // Handle reject button click (opens modal)
  const handleReject = () => {
    setIsRejectModalOpen(true)
  }

  // Handle reject confirmation from modal
  const handleRejectConfirm = (reason: string) => {
    rejectMutation.mutate(
      { userStoryId: userStory.id, reason },
      {
        onSuccess: () => {
          setIsRejectModalOpen(false)
        },
      }
    )
  }

  // Handle Test OK button click
  const handleTestOk = () => {
    setIsTestOkModalOpen(true)
  }

  // Handle Test OK confirmation
  const handleTestOkConfirm = () => {
    testOkMutation.mutate(userStory.id, {
      onSuccess: () => {
        setIsTestOkModalOpen(false)
        toast.success(t('us.testOkSuccess'))
      },
      onError: (error) => {
        toast.error(error.message)
      },
    })
  }

  // Handle Test KO button click
  const handleTestKo = () => {
    setIsTestKoModalOpen(true)
  }

  // Handle Test KO confirmation from modal
  const handleTestKoConfirm = (issueDescription: string) => {
    testKoMutation.mutate(
      { userStoryId: userStory.id, issueDescription },
      {
        onSuccess: () => {
          setIsTestKoModalOpen(false)
          toast.success(t('us.testKoSuccess'))
        },
        onError: (error) => {
          toast.error(error.message)
        },
      }
    )
  }

  // Get test environment URL
  const testEnvUrl = project?.test_environment_url

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />

      <main className="flex flex-1 flex-col overflow-hidden bg-background-secondary">
        {/* Header */}
        <div className="flex flex-col gap-6 px-8 py-6">
          {/* Top row: Back button + Title group + Action buttons */}
          <div className="flex items-start justify-between">
            {/* Left: Back + Title */}
            <div className="flex items-start gap-4">
              {/* Back button */}
              <button
                onClick={() => navigate(`/f/${feature?.short_id}`)}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-white text-foreground transition-colors hover:bg-background-secondary"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>

              {/* Title group */}
              <div className="flex flex-col gap-1">
                {/* Breadcrumb */}
                <nav className="flex items-center gap-1 text-xs text-foreground-muted">
                  <LocalizedLink to="/dashboard" className="hover:text-foreground">
                    {team?.name}
                  </LocalizedLink>
                  <span>/</span>
                  <LocalizedLink to={`/p/${project?.short_id}`} className="hover:text-foreground">
                    {project?.name}
                  </LocalizedLink>
                  <span>/</span>
                  <LocalizedLink to={`/f/${feature?.short_id}`} className="hover:text-foreground">
                    {feature?.name}
                  </LocalizedLink>
                </nav>

                {/* Title + Status */}
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-semibold text-foreground">{userStory.title}</h1>
                  <USStatusBadge status={userStory.status} />
                </div>
              </div>
            </div>

            {/* Right: Action buttons */}
            <div className="flex items-center gap-3">
              {canValidate && (
                <>
                  <button
                    onClick={handleReject}
                    disabled={validateMutation.isPending || rejectMutation.isPending}
                    className="flex h-10 items-center gap-2 rounded-lg border border-border bg-white px-4 text-sm font-medium text-destructive transition-colors hover:bg-red-50 disabled:opacity-50"
                  >
                    <X className="h-4 w-4" />
                    {t('us.reject')}
                  </button>
                  <button
                    onClick={handleValidate}
                    disabled={validateMutation.isPending || rejectMutation.isPending}
                    className="flex h-10 items-center gap-2 rounded-lg bg-green-600 px-4 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
                  >
                    {validateMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                    {validateMutation.isPending ? t('us.validating') : t('us.validateUs')}
                  </button>
                </>
              )}

              {canTest && (
                <>
                  <button
                    onClick={handleTestKo}
                    disabled={testOkMutation.isPending || testKoMutation.isPending}
                    className="flex h-10 items-center gap-2 rounded-lg border border-border bg-white px-4 text-sm font-medium text-destructive transition-colors hover:bg-red-50 disabled:opacity-50"
                  >
                    <X className="h-4 w-4" />
                    {t('us.testKo')}
                  </button>
                  <button
                    onClick={handleTestOk}
                    disabled={testOkMutation.isPending || testKoMutation.isPending}
                    className="flex h-10 items-center gap-2 rounded-lg bg-green-600 px-4 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
                  >
                    {testOkMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                    {testOkMutation.isPending ? t('us.markingTestOk') : t('us.testOk')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Content - Two columns */}
        <div className="flex flex-1 gap-6 overflow-hidden px-8 pb-6">
          {/* Left column - Spec */}
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto">
            {/* Testing instructions - only shown when US is in testing status */}
            {canTest && (
              <div className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-5">
                <p className="text-sm text-amber-800">
                  {t('us.testInstructions')}
                </p>
                {testEnvUrl && (
                  <a
                    href={testEnvUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-accent hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {t('us.testEnvLink')}
                  </a>
                )}
              </div>
            )}

            <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-white">
              {/* Spec Header */}
              <div className="border-b border-border px-5 py-4">
                <h2 className="text-sm font-semibold text-foreground">
                  {t('us.specification')}
                </h2>
              </div>

              {/* Spec Content */}
              <div className="flex flex-col gap-5 overflow-y-auto p-5">
                {/* As a... */}
                <SpecField label={t('us.asALabel')} value={userStory.as_a} />

                {/* I want to... */}
                <SpecField label={t('us.iWantLabel')} value={userStory.i_want} />

                {/* So that... */}
                <SpecField label={t('us.soThatLabel')} value={userStory.so_that} />

                {/* Business Rules */}
                <SpecField label={t('us.businessRules')} value={userStory.business_rules} />

                {/* Acceptance Criteria */}
                <SpecField label={t('us.acceptanceCriteria')} value={userStory.acceptance_criteria} />

                {/* Test Cases */}
                <SpecField label={t('us.testCases')} value={userStory.test_cases} />

                {/* Should Do */}
                <SpecField label={t('us.shouldDo')} value={userStory.should_do} />

                {/* Should Not Do */}
                <SpecField label={t('us.shouldNotDo')} value={userStory.should_not_do} />

                {/* Why */}
                <SpecField label={t('us.why')} value={userStory.why} />
              </div>
            </div>
          </div>

          {/* Right column - Exchange Zone */}
          <UserStoryExchangeZone userStoryId={userStory.id} />
        </div>
      </main>

      {/* Reject Modal */}
      <RejectUSModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        onConfirm={handleRejectConfirm}
        isLoading={rejectMutation.isPending}
        userStoryTitle={userStory.title}
      />

      {/* Test OK Confirmation Modal */}
      <ConfirmModal
        isOpen={isTestOkModalOpen}
        onClose={() => setIsTestOkModalOpen(false)}
        onConfirm={handleTestOkConfirm}
        isLoading={testOkMutation.isPending}
        title={t('us.testOkConfirm')}
        description={t('us.testOkConfirmDesc')}
        confirmLabel={t('us.testOk')}
      />

      {/* Test KO Modal */}
      <TestKOModal
        isOpen={isTestKoModalOpen}
        onClose={() => setIsTestKoModalOpen(false)}
        onConfirm={handleTestKoConfirm}
        isLoading={testKoMutation.isPending}
        userStoryTitle={userStory.title}
      />
    </div>
  )
}
